import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import React, { useState } from 'react';
import Colors from '../../utils/color';
import ConsoleCard from '../MainPageLayout/ConsoleCard';
import MenuBoardTimeTile from './MenuBoardTimeTile';
import ResponseTimeTile from './ResponseTimeTile';
import PulseSignals from './PulseSignals';
import PulseAdvise from './PulseAdvise';
import { useAppSelector } from '../../app/hooks';
import { AIStatusColors } from '../../redux/features/ai/ai.constants';
import {
  selectAIStatus,
  selectIsAIEscalation,
  selectAIEscalationReason,
  selectRequiresTakeover,
  selectIsAIEscalatedOnCarEnter,
  selectIsAIAutoMode,
} from '../../redux/features/ai/ai.selector';
import { selectFeatureFlagUnresolvedItems } from '../../redux/features/config/config.selector';

/**
 * CUSTOMER PULSE (left pane, top card).
 *
 * A collapsible card that gives the human a 2-second read of the live order's AI
 * status. The header (icon + title + collapse chevron) sits inside the white card
 * (matching Action Center); a full-bleed status band pinned at the card's bottom
 * edge encodes the LIVE AI status — green/yellow/red soft tints, plus a prominent
 * vivid blink on takeover.
 *
 * Under the header the card is a compact strip: the Menu Board Time + Response
 * time meters, the live signal + reminder chips (PulseSignals), and the folded-in
 * AI co-pilot ask (PulseAdvise) — with the AI status band pinned at the bottom.
 */

const useStyles = makeStyles((theme: Theme) => ({
  // AI status band (folded-in AIStatusBar): a full-bleed strip pinned at the
  // card's bottom edge (marginTop:auto pushes it down in the flex column).
  // Background encodes the LIVE AI status — green/yellow/red soft tints, plus a
  // prominent vivid blink for a takeover (bandTakeover). The negative margins
  // cancel the card padding so the band reaches the card's inner edges; the
  // card's overflow:hidden + borderRadius round its corners.
  statusBand: {
    flexShrink: 0,
    marginTop: 'auto',
    marginLeft: theme.spacing(-1.25),
    marginRight: theme.spacing(-1.25),
    marginBottom: theme.spacing(-1),
    padding: theme.spacing(0.875, 1.25),
    // Text styled like the "Continue" intent pill: medium weight, 0.72rem.
    fontSize: '0.72rem',
    fontWeight: 500,
    lineHeight: 1.3,
    // One line: truncate with an ellipsis (no wrap, no h-scroll). The full
    // message shows on hover via the title attribute on the band.
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Soft-tint states — one per AIStatusBar state.
  bandGreen: { backgroundColor: '#e3f5e9', color: '#1b7a3d' },
  bandYellow: { backgroundColor: '#fff3d6', color: '#8a5d00' },
  bandRed: { backgroundColor: '#fde3e8', color: '#a8002e' },
  // Car-enter escalation — orange in AIStatusBar; a soft orange tint here.
  bandOrange: { backgroundColor: '#ffe4cc', color: '#9a4a00' },
  // Takeover: a prominent vivid red throb (light↔dark) with white text — a
  // "must not miss" alert, so intentionally NOT a soft tint. Static $keyframes
  // reference (JSS resolves the scoped name only in a static rule).
  '@keyframes statusTakeoverPulse': {
    '0%, 100%': { backgroundColor: '#ff5c5c' },
    '50%': { backgroundColor: '#990000' },
  },
  bandTakeover: {
    color: Colors.white,
    animation: '$statusTakeoverPulse 1s ease-in-out infinite',
  },
  // HITL 3.0 metric tiles row (Menu Board Time + Response time), laid out
  // side-by-side under the header. Each tile self-gates to null, so the row can
  // be empty until one becomes active.
  metricRow: {
    marginTop: theme.spacing(1.25),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

interface CustomerPulseProps {
  // Optional controlled collapse so the parent can drive the resizable panel
  // (collapsing frees its space for the transcript). Falls back to internal
  // state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CustomerPulse = ({
  open: openProp,
  onOpenChange,
}: CustomerPulseProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  // AI status band — wired to the live AI status. Precedence:
  // takeover-blink > agent-intercepted(red) > escalation(red / car-enter orange)
  // > AI status color (green/yellow/red).
  const aiStatus = useAppSelector(selectAIStatus);
  const isAIEscalation = useAppSelector(selectIsAIEscalation);
  const aiEscalationReason = useAppSelector(selectAIEscalationReason);
  const requiresTakeover = useAppSelector(selectRequiresTakeover);
  const isAIEscalatedOnCarEnter = useAppSelector(selectIsAIEscalatedOnCarEnter);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const unresolvedItemsEnabled = useAppSelector(
    selectFeatureFlagUnresolvedItems
  );

  // A takeover was requested (the AI halted). While the AI is still nominally in
  // control (isAIAutoMode) the band BLINKS — a "take over now" alert. The moment
  // the agent takes any manual action, isAIAutoMode flips to false: the takeover
  // has been intercepted, so the blink resolves to a static "Agent interception"
  // in the normal (soft red) tint. requiresTakeover is sticky, so this holds
  // until the session resets for the next order.
  const takeoverActive = unresolvedItemsEnabled && requiresTakeover;
  const showPulse = takeoverActive && isAIAutoMode;
  const agentIntercepted = takeoverActive && !isAIAutoMode;

  const statusMessage = agentIntercepted
    ? 'Agent Intercept'
    : isAIEscalation
    ? aiEscalationReason || aiStatus.message
    : aiStatus.message;
  const bandClass = showPulse
    ? classes.bandTakeover
    : agentIntercepted
    ? classes.bandRed
    : isAIEscalation
    ? isAIEscalatedOnCarEnter
      ? classes.bandOrange
      : classes.bandRed
    : aiStatus.status === AIStatusColors.yellow
    ? classes.bandYellow
    : aiStatus.status === AIStatusColors.red
    ? classes.bandRed
    : classes.bandGreen;

  return (
    <ConsoleCard
      icon={<MonitorHeartOutlinedIcon />}
      title="Pulse"
      open={open}
      onToggle={toggleOpen}
      testId="customer-pulse"
      footer={
        // AI status band ALWAYS renders — the live status (and the takeover
        // blink) must stay visible even when the card is collapsed to a strip.
        // marginTop:auto pins it to the card's bottom edge in both states.
        <div
          className={`${classes.statusBand} ${bandClass}`}
          title={statusMessage}
        >
          {statusMessage}
        </div>
      }
    >
      {/* HITL 3.0 Pulse strip: the Menu Board Time + Response time meter tiles
          (side by side) → the signal + reminder chips → the folded-in AI ask.
          The whole console is already gated on hitl_3_0 at the mount site
          (Body), so no local flag branch is needed. Menu Board Time is always
          shown (freezes on car exit); Response time self-gates to null until the
          agent has intercepted; PulseSignals / PulseAdvise self-gate to null when
          there's nothing to surface. */}
      <div className={classes.metricRow}>
        <MenuBoardTimeTile />
        <ResponseTimeTile />
      </div>
      <PulseSignals />
      <PulseAdvise />
    </ConsoleCard>
  );
};

export default CustomerPulse;
